import { useCallback } from "react";
import { useTheme } from "@mui/material";
import __ from "src/utils/Translations";
import getContrastTextColor from "src/utils/Company/Absences/getContrastTextColor";
import {
  IEmployeeAbsences,
  IAbsenceRequestDay,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

export default () => {
  const theme = useTheme();

  /**
   * Function transforming employee's absence data from API to structure ready for use in DataCalendar component.
   * It adds color property from group config to absence day and filters out data unnecessary for render.
   * @param {object} data - A collection of absence requests created by given employee.
   * @param {boolean} multipleRequestsPerDayAllowed - config option from absence type, decides whether another request can be placed on the same day.
   * @param {string} currentAbsenceRequestId - if transformed on absence request edit form - id to filter out edited request from other requests
   */
  const transformAbsences = useCallback(
    (
      data: IEmployeeAbsences,
      multipleRequestsPerDayAllowed?: boolean,
      currentAbsenceRequestId?: string
    ): [IAbsenceRequestDay[], Date[]] => {
      let daysWithoutMultipleRequest = [];

      const groupInfoMap = data.absenceGroupInfos
        .flat()
        .reduce((acc, group) => {
          acc[group.id] = group.color;
          return acc;
        }, {} as Record<string, string>);

      const absenceTypeInfoMap = data.absenceTypeInfos
        .flat()
        .reduce((acc, absenceType) => {
          acc[absenceType.id] =
            absenceType.configurationOptions.multipleRequestsPerDayAllowed;
          return acc;
        }, {} as Record<string, boolean>);

      // Used to determine multiple request for one day
      const dateFrequencyMap: Record<string, number> = {};
      data.absences.forEach((absence) => {
        dateFrequencyMap[absence.date] =
          (dateFrequencyMap[absence.date] || 0) + 1;
      });

      const absencesData = data.absences
        .filter((absence) => absence.isEffective)
        .filter(
          (absence) => absence.absenceRequestId !== currentAbsenceRequestId
        )
        .map((absence) => {
          const hasMultipleAbsencesOnSameDay =
            dateFrequencyMap[absence.date] > 1;
          // eslint-disable-next-line no-nested-ternary
          const bgColor = hasMultipleAbsencesOnSameDay
            ? theme.palette.grey["800"]
            : absence.status === Status.WAITING_FOR_APPROVAL
            ? theme.palette.grey["200"]
            : groupInfoMap[absence.absenceGroupId] || "#C6D9BE";
          const textColor = getContrastTextColor(bgColor);

          return {
            date: absence.date,
            absenceName: hasMultipleAbsencesOnSameDay
              ? __("Został złożony więcej niż jeden wniosek tego dnia")
              : absence.absenceName,
            bgColor,
            color: textColor,
            absenceRequestId: absence.absenceRequestId,
            multipleRequestsPerDayAllowed:
              absenceTypeInfoMap[absence.absenceTypeId],
          };
        });

      // if current absence type allows another request on the same day,
      // only request's days with absence type not allowing to set multiple requests should be disabled in datepicker
      if (multipleRequestsPerDayAllowed) {
        daysWithoutMultipleRequest = absencesData
          .filter((absence) => !absence.multipleRequestsPerDayAllowed)
          .map((absence) => new Date(absence.date));
      } else {
        // if current absence type isn't allowed to be on the same day as other, every previous request day should be disabled in datepicker
        daysWithoutMultipleRequest = data.absences
          .filter(
            (absence) => absence.absenceRequestId !== currentAbsenceRequestId
          )
          .map((absence) => new Date(absence.date));
      }

      return [absencesData, daysWithoutMultipleRequest];
    },
    [theme.palette.grey]
  );

  return [transformAbsences];
};
